import { Injectable } from '@angular/core';
import { assert } from '@sama/js-core';
import { isUndefined, nth } from '@sama/micro-dash';
import { Camera, Object3D, Plane, Raycaster, Vector2, Vector3 } from 'three';
import { Point2D } from '../../team-apis/otterdam/answers/shape-typings';
import { MainCameraService } from '../render/cameras/main-camera.service';
import { CuboidFace, CuboidHelper } from '../render/mesh/cuboid/cuboid-helper';
import { CuboidService } from '../render/mesh/cuboid/cuboid.service';
import { isCuboidHelper } from '../render/mesh/mesh-utils';
import { LineService } from '../render/mesh/poly-shape/line/line.service';
import { PolygonService } from '../render/mesh/poly-shape/polygon/polygon.service';
import { RendererLayer } from '../render/render-utils';
import { PointCloudContentStore } from '../state/point-cloud-content-store';

@Injectable()
export class RayCastService {
  private raycaster = new Raycaster();

  constructor(
    private cuboidService: CuboidService,
    private polygonService: PolygonService,
    private lineService: LineService,
    private mainCameraService: MainCameraService,
    private store: PointCloudContentStore,
  ) {
    this.raycaster.layers.set(RendererLayer.Main);
  }

  getIntersectingShape(
    mouse = this.store.state().mouse.containerPoint,
    shiftKey = false,
  ) {
    const meshHelper = this.getIntersectingMeshHelper(mouse, shiftKey);
    if (!meshHelper) {
      return undefined;
    }
    return meshHelper;
  }

  getIntersectingCuboidAndFace(
    shiftKey: boolean,
    mouse = this.store.state().mouse.containerPoint,
  ): [CuboidHelper | undefined, CuboidFace | undefined] {
    const meshHelper = this.getIntersectingMeshHelper(mouse, shiftKey);

    if (!meshHelper || !isCuboidHelper(meshHelper)) {
      return [undefined, undefined];
    }

    const intersections = this.raycaster.intersectObjects(
      [meshHelper.getBoxWithoutSegments()],
      false,
    );
    assert(intersections.length > 0, 'Should intersect with identical cuboid');
    const face = this.convertGeoFaceIndexToMaterialFace(
      intersections[0].faceIndex!,
    );

    return [meshHelper, face];
  }

  intersectObjects(
    meshes: Object3D[],
    mouse = this.store.state().mouse.containerPoint,
  ) {
    this.updateRaycaster(mouse);
    return this.raycaster.intersectObjects(meshes, true);
  }

  getPlaneIntersectionPoint(
    plane: Plane,
    mouse = this.store.state().mouse.containerPoint,
  ) {
    this.updateRaycaster(mouse);
    const intersection = new Vector3();
    this.raycaster.ray.intersectPlane(plane, intersection);
    return intersection;
  }

  getRay() {
    return this.raycaster.ray;
  }

  updateRay(containerPoint = this.store.state().mouse.containerPoint) {
    this.updateRaycaster(containerPoint);
  }

  private getIntersectingMeshHelper(mouse: Point2D, shiftKey: boolean) {
    this.updateRaycaster(mouse);

    const cuboids = this.cuboidService.getHelpers().map((cuboid) => cuboid.box);
    const polygons = this.polygonService
      .getHelpers()
      .map((polygon) => polygon.thickLine);
    const lines = this.lineService.getHelpers().map((line) => line.thickLine);

    // Polyshape geometry requires that we intersect all children of the shape
    const intersections = this.raycaster
      .intersectObjects([...cuboids], false)
      .concat(this.raycaster.intersectObjects([...polygons, ...lines], true));

    if (intersections.length === 0) {
      return undefined;
    }

    const intersection = nth(intersections, shiftKey ? -1 : 0);
    const userData = intersection.object.userData;

    if (isUndefined(userData.shapeIndex)) {
      return undefined;
    }

    return this.getShapeHelperByIndex(userData.shapeIndex, userData.meshType);
  }

  private convertGeoFaceIndexToMaterialFace(geometryFaceIndex: number) {
    return Math.floor(geometryFaceIndex / 2);
  }

  private getShapeHelperByIndex(index: number, type: string) {
    if (type === 'Cuboid') {
      return this.cuboidService.getHelper(index);
    } else if (type === 'polyline') {
      return this.lineService.getHelper(index);
    } else if (type === 'polygon') {
      return this.polygonService.getHelper(index);
    } else {
      return undefined;
    }
  }

  private updateRaycaster(
    mouse: Point2D,
    camera: Camera = this.mainCameraService.camera,
  ) {
    this.raycaster.setFromCamera(new Vector2().fromArray(mouse), camera);
  }
}
